import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
} from "@aix/ui"
import { Plus, Upload, Users } from "lucide-react"
import React, { useRef, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

export const AudienceManager: React.FC = () => {
  const [newAudienceName, setNewAudienceName] = useState("")
  const [newAudienceDescription, setNewAudienceDescription] = useState("")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [selectedAudience, setSelectedAudience] = useState<string | null>(null)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: audiences, refetch: refetchAudiences } = trpc.emailMarketing.listAudiences.useQuery()

  const createAudience = trpc.emailMarketing.createAudience.useMutation({
    onSuccess: () => {
      toast.success("Audience created successfully")
      setNewAudienceName("")
      setNewAudienceDescription("")
      setIsCreateDialogOpen(false)
      void refetchAudiences()
    },
    onError: (error) => {
      toast.error(`Failed to create audience: ${error.message}`)
    },
  })

  const importSubscribers = trpc.emailMarketing.importSubscribers.useMutation({
    onSuccess: (result) => {
      toast.success(`Imported ${result.imported} subscribers (${result.skipped} skipped)`)
      setCsvFile(null)
      void refetchAudiences()
    },
    onError: (error) => {
      toast.error(`Import failed: ${error.message}`)
    },
  })

  const handleCreateAudience = () => {
    if (!newAudienceName.trim()) {
      toast.error("Please enter an audience name")
      return
    }

    createAudience.mutate({
      name: newAudienceName,
      description: newAudienceDescription || undefined,
    })
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setCsvFile(file)
    }
  }

  const handleImport = async () => {
    if (!csvFile || !selectedAudience) {
      toast.error("Please select a file and audience")
      return
    }

    const content = await csvFile.text()

    // Parse first row to detect columns
    const lines = content.split("\n")
    const headers = lines[0].split(",").map((h) => h.trim())

    // Auto-detect email column
    const emailColumn =
      headers.find((h) => h.toLowerCase().includes("email") || h.toLowerCase().includes("mail")) || headers[0]

    const firstNameColumn = headers.find((h) => h.toLowerCase().includes("first") || h.toLowerCase().includes("fname"))

    const lastNameColumn = headers.find((h) => h.toLowerCase().includes("last") || h.toLowerCase().includes("lname"))

    importSubscribers.mutate({
      audienceId: selectedAudience,
      csvContent: content,
      mappings: {
        email: emailColumn,
        firstName: firstNameColumn,
        lastName: lastNameColumn,
      },
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Email Audiences</CardTitle>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Audience
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Audience</DialogTitle>
                  <DialogDescription>Create a new email list to organize your subscribers</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">Name</label>
                    <Input
                      value={newAudienceName}
                      onChange={(e) => setNewAudienceName(e.target.value)}
                      placeholder="e.g., Newsletter Subscribers"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">Description</label>
                    <Input
                      value={newAudienceDescription}
                      onChange={(e) => setNewAudienceDescription(e.target.value)}
                      placeholder="Optional description"
                      className="mt-1"
                    />
                  </div>
                  <Button onClick={handleCreateAudience} loading={createAudience.isLoading} className="w-full">
                    Create Audience
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {audiences?.map((audience) => (
              <div
                key={audience.id}
                className="p-4 border border-border rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground">{audience.name}</h3>
                    {audience.description && (
                      <p className="text-sm text-muted-foreground mt-1">{audience.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{audience.subscriberCount} subscribers</span>
                      </div>
                      <Badge variant={audience.isActive ? "default" : "secondary"}>
                        {audience.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelectedAudience(audience.id)}>
                      Select for Import
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {(!audiences || audiences.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                No audiences yet. Create your first audience to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedAudience && (
        <Card>
          <CardHeader>
            <CardTitle>Import Subscribers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Selected audience: {audiences?.find((a) => a.id === selectedAudience)?.name}
                </p>
              </div>

              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />

                {csvFile ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">{csvFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(csvFile.size / 1024).toFixed(2)} KB</p>
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Button onClick={() => setCsvFile(null)} variant="outline" size="sm">
                        Remove
                      </Button>
                      <Button onClick={handleImport} loading={importSubscribers.isLoading} size="sm">
                        Import
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground mb-2">Drop your CSV file here or click to browse</p>
                    <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                      Choose File
                    </Button>
                  </>
                )}
              </div>

              <div className="text-xs text-muted-foreground">
                CSV should have columns for email, firstName (optional), and lastName (optional). The first row should
                contain column headers.
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
